import { HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { RuntimeConfigService } from '@redngapps/feature-flags/data-access';
import { IRuntimeConfig } from '@redngapps/feature-flags/env/types';
import { Nullable } from '@redngapps/shared/types';
import { WINDOW } from '@redngapps/shared/util';
import { isNil } from 'lodash-es';

import { selectFeatureFlagsAccessToken } from '../+state/feature-flags-auth.selectors';

/**
 * Attaches the Google `access_token` as `Authorization: Bearer` so the guard can verify
 * the caller and resolve org-group permissions. Scoped to `/service/**` on this instance only, so the
 * bearer never leaks to `/assets`, other same-origin routes, or third-party hosts (Google's endpoints).
 */
export const authTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const config = inject(RuntimeConfigService).getConfig();
  const appOrigin = inject(WINDOW).location.origin;
  const accessToken = inject(Store).selectSignal(selectFeatureFlagsAccessToken)();
  if (isNil(accessToken) || !targetsBackend(request.url, appOrigin, config)) {
    return next(request);
  }

  return next(request.clone({ setHeaders: { Authorization: `Bearer ${accessToken}` } }));
};

function targetsBackend(url: string, appOrigin: string, config: Nullable<IRuntimeConfig>): boolean {
  let target: URL;
  try {
    // Resolve relative URLs (the reverse-proxied backend) against the app origin.
    target = new URL(url, appOrigin);
  } catch {
    return false;
  }

  if (!target.pathname.startsWith('/service/')) {
    return false;
  }

  const serviceOrigin = isNil(config)
    ? null
    : new URL(`${config.serverProtocol}//${config.serverHost}:${config.serverPort}`).origin;
  return target.origin === appOrigin || target.origin === serviceOrigin;
}
